/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import { RiskScoreBadge } from '../ClientRiskScore/RiskScoreBadge';
import { SKELETON_BLOCK } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';
import { Text, Title, UnstyledButton } from '@/shared/ui';

interface EnrichmentCardProps {
  agencyId: string;
  clientId: string;
  clientDocument?: string;
}

interface EnrichmentInfo {
  unit_price_brl: number;
  currency: string;
  provider: string;
  cached_assessment: { score: number; band: string } | null;
}

/**
 * Sidebar card for Serasa external enrichment. Shows cached score or a
 * CTA button that opens a cost-confirmation modal (R$ 18,79).
 */
export function EnrichmentCard({ agencyId, clientId, clientDocument }: EnrichmentCardProps) {
  const { t } = useTranslation('features/clients');
  const [info, setInfo] = useState<EnrichmentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    fetch(`/api/agency/${agencyId}/clients/${clientId}/enrichment-info`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setInfo)
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, [agencyId, clientId]);

  const handleEnrich = useCallback(async () => {
    setEnriching(true);
    try {
      const resp = await fetch(
        `/api/agency/${agencyId}/clients/${clientId}/risk-assessment`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ document: clientDocument ?? '' }),
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        setInfo((prev) =>
          prev ? { ...prev, cached_assessment: data } : prev,
        );
      }
    } finally {
      setEnriching(false);
      setShowModal(false);
    }
  }, [agencyId, clientId, clientDocument]);

  if (loading) {
    return (
      <SectionCard title={t('sidebar.serasaEnrichment')}>
        <div className="flex flex-col gap-[var(--space-2)]">
          <div className={`${SKELETON_BLOCK} h-6 w-20`} />
          <div className={`${SKELETON_BLOCK} h-3 w-full`} />
        </div>
      </SectionCard>
    );
  }

  if (!info) return null;

  const cached = info.cached_assessment;

  return (
    <>
      <SectionCard title={t('sidebar.serasaEnrichment')}>
        {cached ? (
          <div className="flex items-center justify-between">
            <Text component="span" className="text-lg font-bold text-[var(--text-primary)]">
              {cached.score}
            </Text>
            <RiskScoreBadge band={cached.band} score={cached.score} />
          </div>
        ) : (
          <UnstyledButton
            data-testid="enrich-cta"
            onClick={() => setShowModal(true)}
            className="w-full px-[var(--space-4)] py-[var(--space-2)] text-sm rounded-(--radius-md) bg-[var(--seed-base)] text-[var(--button-primary-text)] hover:opacity-90 transition-opacity"
          >
            {t('sidebar.enrichCta')}
          </UnstyledButton>
        )}
      </SectionCard>

      {showModal && (
        <EnrichmentCostModal
          price={info.unit_price_brl}
          currency={info.currency}
          provider={info.provider}
          loading={enriching}
          onConfirm={handleEnrich}
          onCancel={() => setShowModal(false)}
        />
      )}
    </>
  );
}

interface ModalProps {
  price: number;
  currency: string;
  provider: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation modal shown before a paid client enrichment request.
 *
 * @param props - Price, currency, provider, loading flag, and callbacks.
 * @returns The rendered cost-confirmation modal.
 */
function EnrichmentCostModal({ price, currency, provider, loading, onConfirm, onCancel }: ModalProps) {
  const { t } = useTranslation('features/clients');
  const formatted = `${currency === 'BRL' ? 'R$' : currency} ${price.toFixed(2).replace('.', ',')}`;

  return (
    <div
      data-testid="enrichment-cost-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--surface-inverse)]/50"
    >
      <div className="bg-[var(--surface-primary)] rounded-(--radius-lg) border border-[var(--border-subtle)] p-[var(--space-6)] max-w-[360px] w-full flex flex-col gap-[var(--space-4)]">
        <Title order={3} className="text-base font-bold text-[var(--text-primary)] m-0">
          {t('sidebar.enrichModal.title')}
        </Title>
        <Text className="text-sm text-[var(--text-secondary)] m-0">
          {t('sidebar.enrichModal.description', { price: formatted, provider })}
        </Text>
        <div className="flex justify-end gap-[var(--space-3)]">
          <UnstyledButton
            data-testid="enrichment-cancel"
            onClick={onCancel}
            disabled={loading}
            className="px-[var(--space-4)] py-[var(--space-2)] text-sm rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-secondary)] text-[var(--text-secondary)] cursor-pointer disabled:opacity-50"
          >
            {t('sidebar.enrichModal.cancel')}
          </UnstyledButton>
          <UnstyledButton
            data-testid="enrichment-confirm"
            onClick={onConfirm}
            disabled={loading}
            className="px-[var(--space-4)] py-[var(--space-2)] text-sm rounded-(--radius-md) bg-[var(--seed-base)] text-[var(--button-primary-text)] cursor-pointer disabled:opacity-50"
          >
            {loading
              ? t('sidebar.enrichModal.processing')
              : t('sidebar.enrichModal.confirm')}
          </UnstyledButton>
        </div>
      </div>
    </div>
  );
}
